import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Text,
    mergeClasses,
} from '@fluentui/react-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useBagInfoStyles,
    useBaggageInfoDialogStyles,
    useDataRowStyles,
    useReflightInfoStyles,
} from './BaggageInfoDialog.styles';
import { PassengerIcon } from '../../Icons/PassengerIcon';
import { BaggageIcon } from '../../Icons/BaggageIcon';
import {
    CustomerBag,
    CustomerBagReflightLeg,
    CustomerMishandledBaggage,
    CustomerMishandledBaggageBooking,
    CustomerWithStatuses,
} from '../../../model/customerWithUpdates';
import { fetchMishandledBaggage } from '../../../store/features/customerUpdates/customerUpdates.api';
import CustomerUpdatesDialog from '../components/CustomerUpdatesDialog/CustomerUpdatesDialog';
import GroupBookingTable from './GroupBookingTable';
import { convertBagReFlightLegDateTime } from './BaggageInfoDialog.utils';
import { ICustomerNotificationActionContext } from '../../../store/features/customerUpdates/customerUpdates.types';
import { useCustomerUpdatesDialogResultCallbacks } from '../components/CustomerUpdatesDialog/useCustomerUpdatesDialogResultCallback';
import ItemList, { ItemTab, TabId } from '../components/ItemList/ItemList';
import { customerItemStatusToIconType } from '../../Icons/StyledIcon';

interface CustomerBaggageInfoDialogProps {
    isFlightTeamBoard: boolean;
    open?: boolean;
    customerInfo: CustomerWithStatuses | null;
    onResultClick?: (context: ICustomerNotificationActionContext) => void;
    onOpenChange?: (open: boolean) => void;
}

const DataRow = React.memo(function DataRow({
    label,
    value,
    rootClassName,
    labelClassName,
    valueClassName,
}: {
    label: string;
    value: string | number | React.ReactElement | null | undefined;
    rootClassName?: string;
    labelClassName?: string;
    valueClassName?: string;
}) {
    const styles = useDataRowStyles();

    return (
        <div className={mergeClasses(styles.root, rootClassName)}>
            <Text className={mergeClasses(styles.dataRowLabel, labelClassName)}>
                {label}
            </Text>
            <Text className={mergeClasses(styles.dataRowValue, valueClassName)}>
                {value}
            </Text>
        </div>
    );
});

const ReflightInfo = React.memo(function ReflightInfo({
    bagReFlightLeg,
    className,
    reflightTitle,
}: {
    bagReFlightLeg: CustomerBagReflightLeg;
    className?: string;
    reflightTitle?: string;
}) {
    const { t } = useTranslation();
    const styles = useReflightInfoStyles();
    return (
        <div className={className} data-title={reflightTitle}>
            <DataRow
                label={`${t('CustomerDialog_FlightNo')}:`}
                value={bagReFlightLeg.flightNumber}
                rootClassName={styles.dataRow}
                labelClassName={styles.dataRowLabel}
            />
            <DataRow
                label={`${t('CustomerDialog_Route')}:`}
                value={`${bagReFlightLeg.departureAirportIata} - ${bagReFlightLeg.arrivalAirportIata}`}
                rootClassName={styles.dataRow}
                labelClassName={styles.dataRowLabel}
            />
            <DataRow
                label={`${t('CustomerDialog_DepartureDateTime')}:`}
                value={convertBagReFlightLegDateTime(
                    bagReFlightLeg.scheduledDepartureTime.dateString,
                    bagReFlightLeg.scheduledDepartureTime.timeString
                )}
                rootClassName={styles.dataRow}
                labelClassName={styles.dataRowLabel}
            />
            {!!bagReFlightLeg.scheduledArrivalTime.dateString && (
                <DataRow
                    label={`${t(
                        'FlightBaggageStatus_BaggageInfoDialog_ArrivalDateTime'
                    )}:`}
                    value={convertBagReFlightLegDateTime(
                        bagReFlightLeg.scheduledArrivalTime.dateString,
                        bagReFlightLeg.scheduledArrivalTime.timeString
                    )}
                    rootClassName={styles.dataRow}
                    labelClassName={styles.dataRowLabel}
                />
            )}
        </div>
    );
});

const BagInfo = React.memo(function BagInfo({
    bag,
    showNotificationStatus,
}: {
    bag: CustomerBag;
    showNotificationStatus?: boolean;
}) {
    const { t } = useTranslation();
    const styles = useBagInfoStyles();

    const icon = (
        <BaggageIcon
            width={16}
            height={16}
            type={
                bag.notificationInfo.status === 'Notified'
                    ? 'success'
                    : 'danger'
            }
        />
    );

    return (
        <div className={styles.root}>
            <DataRow
                label={t('CustomerDialog_ReflightInfo')}
                value={showNotificationStatus ? icon : ''}
                rootClassName={styles.dataRow}
                labelClassName={mergeClasses(
                    styles.dataRowLabel,
                    styles.dataRowLabelBold
                )}
            />
            <DataRow
                label={`${t(
                    'FlightBaggageStatus_BaggageInfoDialog_MissingBagReportNumber'
                )}:`}
                value={bag.missingBagReportNumber}
                rootClassName={styles.dataRow}
                labelClassName={styles.dataRowLabel}
            />
            {bag.reflightLegs.map((b, i) => (
                <ReflightInfo
                    className={styles.flight}
                    key={b.id}
                    bagReFlightLeg={b}
                    reflightTitle={`${t('CustomerDialog_Flight')} ${i + 1}`}
                />
            ))}
        </div>
    );
});

const BaggageInfoDialog = ({
    isFlightTeamBoard,
    open,
    customerInfo,
    onResultClick,
    onOpenChange,
}: CustomerBaggageInfoDialogProps) => {
    const { t } = useTranslation();
    const styles = useBaggageInfoDialogStyles();

    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
        customerInfo?.customer.id ?? null
    );

    const [baggage, setBaggage] = useState<CustomerMishandledBaggage | null>(
        null
    );

    const { customer, bags } = useMemo<
        Partial<CustomerMishandledBaggageBooking>
    >(() => {
        if (!customerInfo) {
            return {};
        }

        if (!baggage || selectedCustomerId === null) {
            return { customer: customerInfo.customer };
        }

        const booking = baggage.bookings.find(
            (x) => x.customer.id === selectedCustomerId
        );

        return { ...booking };
    }, [customerInfo, baggage, selectedCustomerId]);

    useEffect(() => {
        if (!bags?.length) {
            return;
        }

        setSelectedBag(bags[0]);
    }, [bags]);

    const [isMobile, setIsMobile] = useState(false);

    const [selectedBag, setSelectedBag] = useState<CustomerBag | null>(null);
    const onTabSelected = useCallback(
        (id: TabId) => setSelectedBag(bags?.find((x) => x.id === id) ?? null),
        [bags, setSelectedBag]
    );

    const notificationStatus = selectedBag?.notificationInfo.status;

    const bagTabs = useMemo<ItemTab[]>(
        () =>
            bags
                ? bags.map((x) => ({
                      id: x.id,
                      label: x.tag,
                  }))
                : [],
        [bags]
    );

    const bagListContent = useMemo(
        () =>
            selectedBag && (
                <BagInfo
                    bag={selectedBag}
                    showNotificationStatus={
                        isMobile && bags && bags.length === 0
                    }
                />
            ),
        [selectedBag, isMobile, bags]
    );

    const bagListIcon = useMemo(
        () =>
            notificationStatus && !isMobile ? (
                <BaggageIcon
                    width={16}
                    height={16}
                    type={customerItemStatusToIconType(notificationStatus)}
                />
            ) : undefined,
        [notificationStatus, isMobile]
    );

    useEffect(() => {
        setSelectedCustomerId(null);
        setBaggage(null);

        if (!customerInfo) {
            return;
        }

        fetchMishandledBaggage(customerInfo.customer.id).then((x) => {
            setSelectedCustomerId(customerInfo.customer.id);
            setBaggage(x.data);
        });
    }, [customerInfo]);

    const { onNotified, onUnableToNotify } =
        useCustomerUpdatesDialogResultCallbacks(
            'MishandledBaggage',
            customer?.id,
            onResultClick
        );

    const otherBookings = useMemo(() => {
        if (selectedCustomerId === null) {
            return [];
        }

        return baggage?.bookings.filter(
            (x) => x.customer.id !== selectedCustomerId
        );
    }, [baggage, selectedCustomerId]);

    const content =
        customer && bags ? (
            <>
                {!!otherBookings?.length && (
                    <Accordion collapsible>
                        <AccordionItem value="GroupBooking">
                            <AccordionHeader
                                className={styles.groupBookingHeader}
                                expandIconPosition="end"
                            >
                                <Text as="h3" size={300} weight="bold">
                                    {t('FlightBaggageStatus_GroupBooking')}
                                </Text>
                            </AccordionHeader>
                            <AccordionPanel style={{ margin: 0 }}>
                                <GroupBookingTable
                                    items={otherBookings}
                                    onSelect={(item) =>
                                        setSelectedCustomerId(item.customer.id)
                                    }
                                />
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                )}
                <ItemList
                    tabs={bagTabs}
                    selectedTabId={selectedBag?.id ?? null}
                    content={bagListContent}
                    title={t('FlightBaggageStatus_BaggageInfoDialog_BagList')}
                    tabLabelPrefix={t(
                        'FlightBaggageStatus_BaggageInfoDialog_BagTaggingNo'
                    )}
                    icon={bagListIcon}
                    onTabSelected={onTabSelected}
                    onIsMobileChange={setIsMobile}
                />
            </>
        ) : undefined;

    return (
        <CustomerUpdatesDialog
            open={open}
            customer={customer}
            status={notificationStatus}
            icon={<PassengerIcon />}
            content={content}
            isFlightTeamBoard={isFlightTeamBoard}
            onOpenChange={onOpenChange}
            onNotified={onNotified}
            onUnableToNotify={onUnableToNotify}
        />
    );
};

export default BaggageInfoDialog;
